/**
 * ChatPanel — persistent chat surface coordinator.
 *
 * Owns refs, scroll behavior, and wiring between hooks and sub-components.
 * Subscribes to the bits of chatStore the shell renders (summarize state). Per-section UI lives next door:
 *   • ChatTokenMeter   — token meter + summarize button
 *   • ChatMessageLog   — message stream + transient cards
 *   • ChatComposer     — textarea + buttons + attachments
 * And per-concern hooks:
 *   • useChatFileUpload      — drag/drop/paste/file-picker
 *   • useChatSend            — send + keyboard
 *   • usePhoneComposerHeight — phone composer drag-to-resize
 */
import { useCallback, useEffect, useRef } from 'react';
import { ConversationHistoryDialog } from '@/components/chat/ConversationHistoryDialog';
import { ConversationSwitcher } from '@/components/chat/ConversationSwitcher';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@onestone/ui/resizable';
import { chatStore, useChatStore } from '@/lib/chatStore';
import { useTranscription } from '@/lib/useTranscription';
import { onChatComposerFocusRequest } from '@/lib/composerFocus';
import { ChatComposer } from './ChatComposer';
import { ChatMessageLog } from './ChatMessageLog';
import { ChatTokenMeter } from './ChatTokenMeter';
import { MediaLightbox } from './MediaLightbox';
import { ModelSelector } from './ModelSelector';
import { TurnMetaBar } from './TurnMetaBar';
import { useChatFileUpload } from './useChatFileUpload';
import { useChatSend } from './useChatSend';
import { usePhoneComposerHeight } from './usePhoneComposerHeight';

export function ChatPanel({ compact = false }: { compact?: boolean } = {}) {
  const {
    conversationId,
    messages,
    isStreaming,
    conversationMeta,
    historyOpen,
    summarizeJob,
    provider,
    model,
    useExtendedContext,
    turnMeta,
  } = useChatStore();

  const logRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const phoneComposer = usePhoneComposerHeight(compact);

  const transcription = useTranscription({
    onResult: (transcript) => {
      const current = chatStore.getInput();
      const sep = current && !current.endsWith(' ') ? ' ' : '';
      chatStore.setInput(current + sep + transcript);
    },
  });

  const upload = useChatFileUpload();
  const { send: rawSend, handleKey } = useChatSend({ isStreaming, transcription, textareaRef });

  const send = useCallback(() => {
    void rawSend();
  }, [rawSend]);

  // ── Scroll ──────────────────────────────────────────────────────────────────
  // Sticky-bottom unless the operator has scrolled up — then we leave their
  // viewport alone so reading old messages isn't interrupted by new arrivals.
  // Dependencies cover messages AND the summarize progress card (transient card
  // that renders below the message stream).
  useEffect(() => {
    const log = logRef.current;
    if (!log) return;
    const onScroll = (): void => {
      userScrolledUp.current = log.scrollHeight - log.scrollTop - log.clientHeight > 80;
    };
    log.addEventListener('scroll', onScroll);
    return () => log.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const log = logRef.current;
    if (log && !userScrolledUp.current) log.scrollTop = log.scrollHeight;
  }, [messages, summarizeJob]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => onChatComposerFocusRequest(() => textareaRef.current?.focus()), []);

  // ── Escape stops streaming when focus is on the textarea ────────────────────
  useEffect(() => {
    if (!isStreaming) return;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key !== 'Escape' || document.activeElement !== textareaRef.current) return;
      e.preventDefault();
      chatStore.stopStreaming();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isStreaming]);

  const isSummarizing = summarizeJob?.running === true;
  const totalNonSystem = messages.filter((m) => m.role !== 'system').length;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid={compact ? 'chat-panel-compact' : 'chat-panel-desktop'}>
      {/* Lightbox portal — one instance for the whole chat panel */}
      <MediaLightbox />
      {/* Header */}
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-border px-4 py-2">
        <div className="shrink-0">
          <ConversationSwitcher />
        </div>
        <ModelSelector />

        <ChatTokenMeter
          conversationMeta={conversationMeta}
          conversationId={conversationId}
          isSummarizing={isSummarizing}
          totalMessages={totalNonSystem}
          model={model}
          provider={provider}
          useExtendedContext={useExtendedContext}
        />
      </header>

      {conversationId.length > 0 && (
        <ConversationHistoryDialog
          open={historyOpen}
          onOpenChange={(open) => chatStore.setHistoryOpen(open)}
          conversationId={conversationId}
        />
      )}

      {/* Composer is shared between layouts; only the split differs. */}
      {(() => {
        const composer = (
          <ChatComposer
            textareaRef={textareaRef}
            fileInputRef={fileInputRef}
            isDragging={upload.isDragging}
            isUploading={upload.isUploading}
            transcription={transcription}
            onDragEnter={upload.handleDragEnter}
            onDragLeave={upload.handleDragLeave}
            onDragOver={upload.handleDragOver}
            onDrop={upload.handleDrop}
            onFilePickerChange={upload.handleFilePickerChange}
            onPaste={upload.handlePaste}
            onKey={handleKey}
            onSend={send}
          />
        );

        // Compact (phone): flex column with a draggable composer
        // (usePhoneComposerHeight owns the height).
        if (compact) {
          return (
            <div ref={phoneComposer.containerRef} className="flex min-h-0 flex-1 flex-col">
              {/* Message log takes all remaining space above the composer */}
              <div className="min-h-0 flex-1">
                <ChatMessageLog logRef={logRef} textareaRef={textareaRef} />
              </div>

              {/* Turn injection metadata — what was injected into the agent's context */}
              {turnMeta && <TurnMetaBar meta={turnMeta} />}

              {/* Drag handle + composer at the bottom */}
              <div className="flex shrink-0 flex-col border-t border-border" style={{ height: phoneComposer.height }}>
                {/* Drag pill — wide touch target, visual pill indicator */}
                <div
                  className="flex w-full shrink-0 cursor-grab touch-none items-center justify-center py-2 active:cursor-grabbing"
                  onTouchStart={phoneComposer.onTouchStart}
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label="Resize chat input area"
                  aria-valuenow={phoneComposer.height}
                  aria-valuemin={phoneComposer.minHeight}
                >
                  <div className="h-1 w-10 rounded-full bg-muted-foreground/30" />
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{composer}</div>
              </div>
            </div>
          );
        }

        // Desktop: resizable vertical split (log over composer). The meta bar
        // lives INSIDE the composer panel: a panel group lays out only its
        // panels, so a bare child between them takes space the panels were
        // sized to have.
        return (
          <ResizablePanelGroup direction="vertical" autoSaveId="onestone-chat-split" className="min-h-0 flex-1">
            <ResizablePanel defaultSize={72} minSize={25} className="min-h-0">
              <ChatMessageLog logRef={logRef} textareaRef={textareaRef} />
            </ResizablePanel>
            <ResizableHandle />
            <ResizablePanel defaultSize={28} minSize={12} className="flex min-h-0 flex-col">
              {turnMeta && <TurnMetaBar meta={turnMeta} />}
              <div className="min-h-0 flex-1">{composer}</div>
            </ResizablePanel>
          </ResizablePanelGroup>
        );
      })()}
    </div>
  );
}
